import { useContext, createContext, useEffect, useCallback, useReducer, useMemo, Fragment } from 'react';
import { jsx, jsxs } from 'react/jsx-runtime';

/**
 * Variable checking the server environment runtime.
 */ const IS_SERVER_ENVIRONMENT = typeof window === "undefined";
/**
 * Variable to detect the test environment.
 * Testing runtimes (Jest and Vitest) automatically set the `NODE_ENV` environment variable to `test`.
 * Thanks to the `DefinePlugin` setup, the bundler minifier automatically strips the conditional code statement if NODE_ENV !== "test".
 * @see {@link https://vitest.dev/guide/migration.html#envs Node environment injection}.
 */ process.env.NODE_ENV === "test";
const LANGUAGE_TAGS_VALUES = [
    "de-DE",
    "de-AT",
    "en-GB",
    "fr-BE",
    "fr-FR",
    "nl-BE"
];
new Set(LANGUAGE_TAGS_VALUES);
new Set(LANGUAGE_TAGS_VALUES.map((tag)=>tag.split("-")[0]));
new Set(LANGUAGE_TAGS_VALUES.map((tag)=>tag.split("-")[1]));
/**
 * A factory to format an error message in a consistent way.
 * @param configuration The configuration object.
 * @returns The formatted error message.
 */ const createErrorMessage = ({ cause, solution })=>{
    return `❌ An error occurred: ${cause}.\n✅ To solve the issue: ${solution}.`;
};
/**
 * A factory to uniformize the way global module variables are named.
 * It prefixes each module by the Unified Frontend {@link https://github.com/axel-springer-kugawana/aviv_platform_unified_frontend_registry/blob/6d544d0fb00088c7af670924bb25150128a45285/libraries/registry/types/index.ts#L139 capability code}.
 * @param module - The module name.
 * @param component - Optional component name to append.
 * @returns The global key name derivated from the input (used to scope global states linked to a module).
 */ const createGlobalKey = (module, component)=>{
    return `__UFRN_${module.toUpperCase()}${"" }__`;
};
createGlobalKey("BUILD_FINGERPRINTS");
const IsIntantiatedContext = /*#__PURE__*/ createContext(false);
/**
 * A higher-order-factory-like helper to enforce a single component instance in the tree.
 * @param componentName The component name.
 * @param Component The targetted React component to decorate with the singleton behavior (most of the time, Provider-like component).
 * @returns The decorated component with singleton behavior.
 */ const createSingletonComponent = (componentName, Component)=>{
    return (props)=>{
        const isInstantiated = useContext(IsIntantiatedContext);
        if (isInstantiated) {
            console.error(createErrorMessage({
                cause: `The \`${componentName}\` component cannot be instantiated more than once`,
                solution: `The singleton behavior has been automatically enforced by skipping the nested \`${componentName}\` element creation. To remove the log and prevent any introduced side effect(s), please remove any \`${componentName}\` component instance in your component tree outside the sandbox entry point.\nIf the log still persists, make sure the sandbox entry point is not included in your production bundle`
            }));
            return props.children;
        }
        return /*#__PURE__*/ jsx(IsIntantiatedContext, {
            value: true,
            children: /*#__PURE__*/ jsx(Component, {
                ...props
            })
        });
    };
};
/**
 * A React hook to enforce the Provider definition before using a context.
 * @param scope Metadata describing the context scope (including package and module name).
 * @param context The React `Context` to check.
 * @returns The context value.
 */ const useSafeContext = (scope, context)=>{
    const value = useContext(context);
    if (value === null) {
        // Apply the fail-fast principle to prevent any issues in production (e.g. the ones with default values
        // extraneous the current deployment target (such as applying `AVIV_THEME` while the current brand is SeLoger))
        throw new Error(createErrorMessage({
            cause: `The \`${scope.module}\` module is not able to retrieve contextual data`,
            solution: `Verify your Shell integration by checking if the \`${scope.package}\` package is effectively shared.\nIf the issue is only reproducible in your local environment, make sure to simulate the Shell environment with the right component(s)`
        }));
    }
    return value;
};
function useThirdPartyScript({ src, onLoad }) {
    useEffect(()=>{
        const existingScript = document.querySelector(`script[src="${src}"]`);
        if (existingScript) {
            return;
        }
        const script = document.createElement("script");
        script.src = src;
        script.async = true;
        if (typeof onLoad === "function") {
            script.onload = ()=>{
                onLoad();
            };
        }
        document.body.appendChild(script);
    }, [
        src,
        onLoad
    ]);
}

const GLOBAL_KEY = createGlobalKey("authentication");

class InvalidTokenError extends Error {
}
InvalidTokenError.prototype.name = "InvalidTokenError";
function b64DecodeUnicode(str) {
    return decodeURIComponent(atob(str).replace(/(.)/g, (m, p) => {
        let code = p.charCodeAt(0).toString(16).toUpperCase();
        if (code.length < 2) {
            code = "0" + code;
        }
        return "%" + code;
    }));
}
function base64UrlDecode(str) {
    let output = str.replace(/-/g, "+").replace(/_/g, "/");
    switch (output.length % 4) {
        case 0:
            break;
        case 2:
            output += "==";
            break;
        case 3:
            output += "=";
            break;
        default:
            throw new Error("base64 string is not of the correct length");
    }
    try {
        return b64DecodeUnicode(output);
    }
    catch (err) {
        return atob(output);
    }
}
function jwtDecode(token, options) {
    if (typeof token !== "string") {
        throw new InvalidTokenError("Invalid token specified: must be a string");
    }
    options || (options = {});
    const pos = options.header === true ? 0 : 1;
    const part = token.split(".")[pos];
    if (typeof part !== "string") {
        throw new InvalidTokenError(`Invalid token specified: missing part #${pos + 1}`);
    }
    let decoded;
    try {
        decoded = base64UrlDecode(part);
    }
    catch (e) {
        throw new InvalidTokenError(`Invalid token specified: invalid base64 for part #${pos + 1} (${e.message})`);
    }
    try {
        return JSON.parse(decoded);
    }
    catch (e) {
        throw new InvalidTokenError(`Invalid token specified: invalid json for part #${pos + 1} (${e.message})`);
    }
}

const appendQueryParam = (baseUrl, key, value)=>{
    const u = new URL(baseUrl, "http://dummy-base"); // using a dummy base to handle relative URLs
    u.searchParams.set(key, value);
    if (/^https?:\/\//.test(baseUrl)) {
        return u.toString();
    }
    return `${u.pathname}${u.search}${u.hash}`;
};
const toBase64 = (s)=>typeof window !== "undefined" && typeof btoa === "function" ? btoa(new TextEncoder().encode(s).reduce((data, byte)=>data + String.fromCharCode(byte), "")) : Buffer.from(s, "utf-8").toString("base64");
const createRedirectionUrl = (from, to, redirectType)=>{
    const target = redirectType === "absolute" ? to.href : to.pathname + to.search;
    const current = new URL(to.href);
    const m = current.searchParams.get("m");
    const payload = {
        redirectUri: target
    };
    if (m) payload.m = m;
    const state = toBase64(JSON.stringify(payload));
    return appendQueryParam(`${from}${encodeURIComponent(target)}`, "state", state);
};
const redirect = (url, redirectType)=>{
    if (!IS_SERVER_ENVIRONMENT) {
        window.location.href = createRedirectionUrl(url, window.location, redirectType);
    }
};
const getDocumentCookie = (expirationCookieKey)=>{
    if (IS_SERVER_ENVIRONMENT || !expirationCookieKey) return undefined;
    const cookieMatchArray = document.cookie.match(`(^|;)\\s*${expirationCookieKey}\\s*=\\s*([^;]+)`);
    return cookieMatchArray ? cookieMatchArray.pop() : undefined;
};
/**
 * refreshToken
 * @description ⚠️ Function meant to be consumed by the shell only.
 */ const isTokenExpired = (token)=>{
    try {
        // TODO: The `isTokenExpired` logic must be updated to source the expiration information from the `expiration` cookie.
        // This cookie is used, like an ID Token (OpenID Connect), as a non-restricted cookie (accessible by client-side code / non-HttpOnly target) to check the validity of an user session.
        // For now, it's not used as the expiration time is not handled correctly Firefly side but it must be the targetted implementation once the issue is fixed.
        const threshold = 10000; // 10s
        const { exp } = decodeJwtOrThrow(token);
        const expDate = Number(exp) * 1000 - threshold;
        return expDate < Date.now();
    } catch  {
        return true;
    }
};
const decodeJwt = (token)=>{
    try {
        return jwtDecode(token);
    } catch  {
        return null;
    }
};
const decodeJwtOrThrow = (token)=>{
    try {
        return jwtDecode(token);
    } catch (error) {
        throw new Error(createErrorMessage({
            cause: `Failed to decode the JWT token - ${error}`,
            solution: "Try again later or contact the support team if the issue persists"
        }), {
            cause: error
        });
    }
};
/**
 * Global refresh lock.
 *
 * This variable holds a reference to the current in-flight refresh request (if any).
 * If multiple calls to `refreshToken()` happen simultaneously, they will all share the same promise
 * — ensuring that only **one network request** is actually made to the backend.
 *
 * Once the refresh process completes (successfully or not), the lock is released (set back to null).
 * This prevents redundant `/refresh` calls and avoids race conditions between components.
 */ let refreshPromise = null;
/**
 * refreshToken
 * @description ⚠️ Function meant to be consumed by the shell only.
 * ⚠️ For security reasons the fetch API force the browser to send a preflighted request (OPTIONS) before the actual request.
 * This is a security feature to prevent CORS requests from being sent to a server without the server's consent.
 * We enforce the "Content-Type" to "plain/text" to avoid the preflighted request.
 * Currently, API for Immowelt and Immonet aren't configured to handle preflighted requests.
 * BEWARE: This is a workaround and should be removed once the API is configured to handle preflighted requests.
 *
 */ const refreshToken = async (refreshUrl, forwardedCookieRequestHeader)=>{
    if (refreshPromise) {
        return refreshPromise;
    }
    if (refreshUrl.length === 0) {
        return {
            isRefreshed: false,
            error: "Refresh URL is not provided"
        };
    }
    const reqInit = {
        method: "GET",
        headers: {
            "Content-Type": "text/plain",
            ...IS_SERVER_ENVIRONMENT && forwardedCookieRequestHeader && {
                Cookie: forwardedCookieRequestHeader
            }
        },
        ...!IS_SERVER_ENVIRONMENT && {
            credentials: "include"
        }
    };
    refreshPromise = fetch(refreshUrl, reqInit).then((res)=>{
        if (res.status === 200) {
            return {
                isRefreshed: true,
                response: {
                    headers: res.headers
                }
            };
        } else {
            return {
                isRefreshed: false,
                error: res.statusText
            };
        }
    }).catch((error)=>{
        return {
            isRefreshed: false,
            error
        };
    }).finally(()=>{
        refreshPromise = null;
    });
    return refreshPromise;
};
const getCookieData = async (chechCookiesUrl)=>{
    const reqInit = {
        method: "GET",
        headers: {
            "Content-Type": "text/plain"
        },
        credentials: "include"
    };
    return await fetch(chechCookiesUrl, reqInit).then((res)=>{
        return res.json();
    }).catch((error)=>{
        return error;
    });
};
const getAccessToken = (accessToken)=>{
    const token = getDocumentCookie(accessToken);
    return {
        value: token,
        isExpired: token ? isTokenExpired(token) : true
    };
};

const useRuntimeContext = (moduleName, context)=>useSafeContext({
        package: "@aviv-unified-frontend/runtime",
        module: moduleName
    }, context);

const AuthenticationContext = /*#__PURE__*/ createContext(null);
const INTERNAL_AUTHENTICATION_KEY = Symbol(GLOBAL_KEY);
/**
 * Create authentication context
 * @description ⚠️ Function meant to be consumed by the shell only.
 */ const createAuthenticationContext = (initialValue = {})=>{
    const { config, unsafe_accessToken, identityProvider } = initialValue;
    let context;
    if (IS_SERVER_ENVIRONMENT) {
        context = {
            config,
            unsafe_accessToken,
            identityProvider
        };
    } else {
        context = {
            config,
            unsafe_accessToken: getDocumentCookie(config?.cookies.accessToken) ?? unsafe_accessToken,
            identityProvider
        };
    }
    return {
        [INTERNAL_AUTHENTICATION_KEY]: context
    };
};
const useAuthenticationContext = ()=>useRuntimeContext("authentication", AuthenticationContext);

const initializeGoogleOneTap = ({ config, urls, onDismiss })=>{
    if (!window.google?.accounts?.id) {
        console.warn("Google One Tap API not available.");
        onDismiss();
        return;
    }
    window.google.accounts.id.initialize({
        client_id: config.googleClientId,
        callback: (response)=>{
            const credential = response.credential;
            const jwtPayload = decodeJwt(credential);
            if (!jwtPayload) return;
            const baseUrl = new URL("/", urls.signIn).origin;
            const params = new URLSearchParams({
                client_id: config.auth0.clientId,
                response_type: "token",
                redirect_uri: window.location.href,
                connection: "google-oauth2",
                login_hint: jwtPayload?.email,
                audience: config.auth0.audience.toLowerCase()
            });
            window.location.href = `${baseUrl}/authorize?${params.toString()}`;
        }
    });
    window.google.accounts.id.prompt(()=>{
        /**
     * When FedCM is active, PromptMomentNotification is not triggered.
     * @see https://developers.google.com/identity/gsi/web/reference/js-reference?hl=en#PromptMomentNotification
     */ onDismiss();
    });
};
const GoogleOneTap = ({ config, urls, onDismiss })=>{
    const initialize = useCallback(()=>{
        initializeGoogleOneTap({
            config,
            urls,
            onDismiss
        });
    }, [
        config,
        urls,
        onDismiss
    ]);
    useThirdPartyScript({
        src: "https://accounts.google.com/gsi/client",
        onLoad: initialize
    });
    return null;
};
const GoogleOneTapLoader = ({ config, urls })=>{
    const { useGoogleOneTap } = config;
    const { shouldInit, onDismiss } = useGoogleOneTap();
    if (!shouldInit) return null;
    return /*#__PURE__*/ jsx(GoogleOneTap, {
        config: config,
        urls: urls,
        onDismiss: onDismiss
    });
};

const reducer = (state, action)=>{
    const { type } = action;
    switch(type){
        case "pending":
            return {
                ...state,
                state: "pending",
                error: null
            };
        case "fulfilled":
            return {
                ...state,
                state: action.payload.state,
                unsafe_accessToken: action.payload.unsafe_accessToken,
                error: null
            };
        case "rejected":
            return {
                ...state,
                state: "error",
                error: action.payload.error,
                unsafe_accessToken: undefined
            };
        default:
            return state;
    }
};
const defaultContext = createAuthenticationContext();
/**
 * AuthenticationProvider
 * @description ⚠️ Function meant to be consumed by the shell only.
 */ const AuthenticationProvider = createSingletonComponent("AuthenticationProvider", ({ children, context = defaultContext })=>{
    const { config, unsafe_accessToken, identityProvider } = context[INTERNAL_AUTHENTICATION_KEY];
    const [data, dispatch] = useReducer(reducer, {
        state: "pending",
        error: null,
        unsafe_accessToken
    });
    const value = useMemo(()=>({
            config,
            data,
            dispatch,
            unsafe_accessToken,
            useAuthentication: identityProvider?.useAuthentication
        }), [
        config,
        data,
        dispatch,
        unsafe_accessToken,
        identityProvider
    ]);
    const ResolvedIdentityProvider = identityProvider?.Component ?? Fragment;
    return /*#__PURE__*/ jsx(ResolvedIdentityProvider, {
        children: /*#__PURE__*/ jsxs(AuthenticationContext, {
            value: value,
            children: [
                children,
                config?.googleOneTap && /*#__PURE__*/ jsx(GoogleOneTapLoader, {
                    config: config.googleOneTap,
                    urls: config.urls
                })
            ]
        })
    });
});

const useHttpOnlyAuthenticationStrategy = ()=>{
    const { config, data, dispatch, useAuthentication } = useAuthenticationContext();
    const { state, error, unsafe_accessToken } = data;
    const { authenticate } = useAuthentication?.() ?? {};
    const authenticateCallback = useCallback(async ({ shouldRedirect = false })=>{
        try {
            dispatch({
                type: "pending"
            });
            if (!config) {
                return dispatch({
                    type: "rejected",
                    payload: {
                        error: new Error("Authentication configuration is missing.")
                    }
                });
            }
            const isAuthenticated = authenticate && await authenticate();
            if (!isAuthenticated && shouldRedirect) {
                return redirect(config.urls.signIn, "relative");
            }
            dispatch({
                type: "fulfilled",
                payload: {
                    state: isAuthenticated ? "authenticated" : "unauthenticated",
                    unsafe_accessToken: undefined
                }
            });
        } catch (error) {
            dispatch({
                type: "rejected",
                payload: {
                    error
                }
            });
        }
    }, [
        config,
        dispatch,
        authenticate
    ]);
    const getUnsafeAccessToken = useCallback(async ()=>{
        if (!config) return undefined;
        if (IS_SERVER_ENVIRONMENT) {
            return unsafe_accessToken;
        }
        await authenticateCallback({
            shouldRedirect: false
        });
        return undefined;
    }, [
        authenticateCallback,
        config,
        unsafe_accessToken
    ]);
    const isDisabled = config === null || config === undefined;
    return useMemo(()=>({
            /**
         * As a security measure, the access token is stored as an HTTP-only cookie.
         * This means that it cannot be accessed via JavaScript, helping to protect it from XSS attacks.
         *
         * On the client side, the value of `unsafe_accessToken` will always be `undefined` by specification.
         */ unsafe_accessToken,
            /**
         * UI state that reflects a potentially staled authentication state performed on the client side.
         * It is only meant to serve as an indicative value, not as
         * a source of truth. The true source of authentication and authorization
         * must be the backend (Lambda function `authorizer`).
         * Each application should ensure proper protection of their data and back-end
         * functionalities by relying on secure server-side checks and validations.
         */ state,
            error,
            isDisabled,
            authenticate: authenticateCallback,
            /**
         * This getter will prompt a re-authentication to ensure the most up-to-date
         * authentication state. However, since the access token is stored as an HTTP-only cookie,
         * it cannot be accessed directly from JavaScript run browser side (e.g., via `document.cookie`). Therefore, this function will always return `undefined` on the browser side.
         */ getUnsafeAccessToken
        }), [
        state,
        error,
        isDisabled,
        unsafe_accessToken,
        authenticateCallback,
        getUnsafeAccessToken
    ]);
};

const useNonHttpOnlyAuthenticationStrategy = ()=>{
    const { config, data, dispatch } = useAuthenticationContext();
    const { state, unsafe_accessToken, error } = data;
    const authenticate = useCallback(async ({ shouldRedirect = false })=>{
        try {
            dispatch({
                type: "pending"
            });
            if (!config) {
                return dispatch({
                    type: "rejected",
                    payload: {
                        error: new Error("Authentication configuration is missing.")
                    }
                });
            }
            const token = getAccessToken(config.cookies.accessToken);
            const hasSessionExpirationCookie = Boolean(getDocumentCookie(config.cookies.sessionExpiration));
            const hasSession = !token.isExpired || hasSessionExpirationCookie;
            if (!hasSession) {
                return shouldRedirect ? redirect(config.urls.signIn, "absolute") : dispatch({
                    type: "rejected",
                    payload: {
                        error: new Error("Access token is missing.")
                    }
                });
            }
            const shouldAttemptRefresh = token.isExpired && hasSessionExpirationCookie;
            if (!shouldAttemptRefresh) {
                const unsafe_accessToken = token.value;
                return dispatch({
                    type: "fulfilled",
                    payload: {
                        state: "authenticated",
                        unsafe_accessToken
                    }
                });
            }
            const res = await refreshToken(config.urls.refresh);
            const { isRefreshed } = res;
            if (!isRefreshed && shouldRedirect) {
                return redirect(config.urls.signIn, "absolute");
            }
            if (!isRefreshed) {
                return dispatch({
                    type: "rejected",
                    payload: {
                        error: res.error
                    }
                });
            }
            const unsafe_accessToken = getDocumentCookie(config.cookies.accessToken);
            dispatch({
                type: "fulfilled",
                payload: {
                    state: isRefreshed ? "authenticated" : "unauthenticated",
                    unsafe_accessToken
                }
            });
        } catch (error) {
            dispatch({
                type: "rejected",
                payload: {
                    error
                }
            });
        }
    }, [
        config,
        dispatch
    ]);
    const getUnsafeAccessToken = useCallback(async ()=>{
        if (!config) return undefined;
        if (IS_SERVER_ENVIRONMENT) return unsafe_accessToken;
        await authenticate({
            shouldRedirect: false
        });
        return getDocumentCookie(config.cookies.accessToken);
    }, [
        authenticate,
        config,
        unsafe_accessToken
    ]);
    useEffect(()=>{
        if (config) {
            authenticate({
                shouldRedirect: false
            }).catch((error)=>{
                console.warn("Authentication failed:", error);
            });
        }
    }, [
        config,
        authenticate
    ]);
    const isDisabled = config === null || config === undefined;
    return useMemo(()=>({
            state,
            error,
            unsafe_accessToken,
            isDisabled,
            authenticate,
            /**
         * This getter will prompt a re-authentication to ensure the most up-to-date authentication state.
         * Then, it will return the current access token from the document cookies.
         */ getUnsafeAccessToken
        }), [
        state,
        error,
        unsafe_accessToken,
        isDisabled,
        authenticate,
        getUnsafeAccessToken
    ]);
};

const useAuthentication = ()=>{
    const { useAuthentication } = useRuntimeContext("authentication", AuthenticationContext);
    const useStrategy = useAuthentication ? useHttpOnlyAuthenticationStrategy : useNonHttpOnlyAuthenticationStrategy;
    return useStrategy();
};

const TO_GUARD_STATE_MAPPER = {
    authenticated: "authorized",
    unauthenticated: "unauthorized",
    pending: "pending",
    error: "error"
};
/**
 * createUseAuthenticationGuard
 * @description ⚠️ Function meant to be consumed by the shell only.
 */ const createUseAuthenticationGuard = ({ shouldRedirect })=>function useAuthenticationGuard() {
        const { state, authenticate, isDisabled } = useAuthentication();
        useEffect(()=>{
            const checkAuthentication = async ()=>{
                await authenticate({
                    shouldRedirect
                });
            };
            if (!isDisabled) {
                checkAuthentication();
            }
        }, [
            authenticate,
            isDisabled
        ]);
        return TO_GUARD_STATE_MAPPER[state];
    };

export { AuthenticationProvider, createAuthenticationContext, createRedirectionUrl, createUseAuthenticationGuard, decodeJwt, getCookieData, getDocumentCookie, isTokenExpired, refreshToken, useAuthentication };
